import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, isSameWeek, addDays } from 'date-fns';
import Select from 'react-select';
import './ViewCurriculum.css';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
const CreateCurriculum = () => {
  const calendarRef = useRef(null);
  const { userData } = useUser();
  const userId = userData?.userId;
  const [level, setLevel] = useState(null);
  const [subLevel, setSubLevel] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [curriculumIds, setCurriculumIds] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDrills, setSelectedDrills] = useState({
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: []
  });

  const fetchCurriculum = useCallback(async () => {
    if (!selectedSport || !level || !subLevel) return;
    setLoading(true);
    const startDate = format(selectedStartDate, 'yyyy-MM-dd');
    const endDate = format(endOfWeek(selectedStartDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');

    try {
      const subLevelParam = encodeURIComponent(subLevel.label);
      const response = await fetch(
        `${process.env.REACT_APP_API_GET_CURRICULUM}?sportId=${selectedSport.value}&level=${level.value}&subLevel=${subLevelParam}&startDate=${startDate}&endDate=${endDate}`,
        { headers: { 'x-authorization': process.env.REACT_APP_AUTH_TOKEN } }
      );

      if (!response.ok) throw new Error('Failed to fetch curriculum');

      const data = await response.json();

      if (data.success) {
        const newSelectedDrills = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
        const newCurriculumIds = {};

        data.data.curriculums.forEach((curriculum) => {
          const rawDate = new Date(curriculum.date);
          const dayOfWeek = rawDate.getDay();
          const dateString = format(rawDate, 'yyyy-MM-dd');

          newCurriculumIds[dateString] = curriculum.curriculumId;
          const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dayOfWeek];

          curriculum.drills.forEach((drillItem) => {
            const drillsArray = Array.isArray(drillItem.drill) ? drillItem.drill : [drillItem.drill];
            drillsArray.forEach((drillData) => {
              newSelectedDrills[dayKey].push({
                value: drillData.id,
                label: drillData.name,
                categoryName: drillItem.categoryName,
              });
            });
          });
        });

        setCurriculumIds(newCurriculumIds);
        setSelectedDrills(newSelectedDrills);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load curriculum data',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedSport, level, subLevel, selectedStartDate]);

  useEffect(() => {
    fetchCurriculum();
  }, [fetchCurriculum]);

  const [applyDates, setApplyDates] = useState({
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: []
  });
  const addDate = (dayKey) => {
    setApplyDates(prev => ({
      ...prev,
      [dayKey]: [...prev[dayKey], '']
    }));
  };
  const handleDateChange = (dayKey, index, newDate) => {
    const newDates = [...applyDates[dayKey]];
    const isAdded = newDates.find(date => date === newDate);
    if (isAdded) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Dates cannot be duplicate for same day',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    };
    newDates[index] = newDate;
    setApplyDates(prev => ({
      ...prev,
      [dayKey]: newDates
    }));
  };
  const removeDate = (dayKey, index) => {
    const newDates = [...applyDates[dayKey]];
    newDates.splice(index, 1);
    setApplyDates(prev => ({
      ...prev,
      [dayKey]: newDates
    }));
  };

  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [drillsData, setDrillsData] = useState([]);

  useEffect(() => {
    const fetchDrills = async () => {
      if (!selectedSport) {
        setDrillsData([]);
        return;
      }

      try {
        const response = await fetch(process.env.REACT_APP_API_GET_ALL_DRILLS, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch drills');
        const responseData = await response.json();
        const allDrills = responseData.data.drills || [];

        const groupedDrills = allDrills
          .filter(drill => drill.sportId === selectedSport.value)
          .reduce((acc, drill) => {
            if (!acc[drill.categoryName]) {
              acc[drill.categoryName] = { label: drill.categoryName, options: [] };
            }
            acc[drill.categoryName].options.push({ value: drill.id, label: drill.name });
            return acc;
          }, {});

        const formattedDrills = Object.values(groupedDrills);

        setDrillsData(formattedDrills);
      } catch (error) {
        setDrillsData([]);
      }
    };

    fetchDrills();
  }, [selectedSport]);


  const getAvailableDrills = (dayKey) => {
    const selectedDrillsForDay = selectedDrills[dayKey] || [];

    return drillsData.map(category => ({
      ...category,
      options: category.options.filter(
        drill => !selectedDrillsForDay.some(selected => selected.value === drill.value)
      )
    })).filter(category => category.options.length > 0);
  };

  const levelOptions = selectedSport
    ? Object.keys(selectedSport)
      .filter(key =>
        key !== "value" &&
        key !== "label" &&
        Array.isArray(selectedSport[key])
      )
      .map(proficiency => ({
        value: proficiency,
        label: proficiency.charAt(0) + proficiency.slice(1).toLowerCase()
      }))
    : [];

  const subLevelOptions = level && selectedSport
    ? selectedSport[level.value].map((sub, index) => ({
      value: sub.subLevelName,
      label: sub.subLevelName || `${level.label} ${index + 1}`
    }))
    : [];

  const fetchSportsOptions = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_GET_ALL_LEVEL_SUBLEVEL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch sports data");
      }

      const responseData = await response.json();
      const rawData = responseData?.data?.data || [];
      const formattedData = rawData.map((sport) => ({
        value: sport.sportId,
        label: sport.sportName || `Sport ${sport.sportId}`,
        ...Object.keys(sport)
          .filter((key) => key !== "sportId" && key !== "sportName")
          .reduce((acc, level) => {
            if (Array.isArray(sport[level])) {
              acc[level] = sport[level].map(({ id, subLevelName, sportProficiencyId }) => ({
                id,
                subLevelName,
                sportProficiencyId,
              }));
            } else {
              acc[level] = [];
            }
            return acc;
          }, {}),
      }));

      return formattedData;
    } catch (error) {
      return [];
    }
  };
  const [sportsOptions, setSportsOptions] = useState([]);

  useEffect(() => {
    const getSportsData = async () => {
      const data = await fetchSportsOptions();
      setSportsOptions(data);
    };

    getSportsData();
  }, []);


  const weekDays = eachDayOfInterval({
    start: selectedStartDate,
    end: endOfWeek(selectedStartDate, { weekStartsOn: 1 })
  });

  const handleLevelChange = (selectedOption) => {
    setLevel(selectedOption);
    setSubLevel(null);
  };

  const handleSubLevelChange = (selectedOption) => {
    setSubLevel(selectedOption);
  };

  const handleDrillSelect = (day, selectedOption) => {
    if (!selectedOption) return;

    const category = drillsData.find(cat =>
      cat.options.some(opt => opt.value === selectedOption.value)
    );

    setSelectedDrills(prev => ({
      ...prev,
      [day]: [...prev[day], {
        ...selectedOption,
        categoryName: category?.label || 'Uncategorized'
      }]
    }));
  };

  const handleApplyDateChange = (day, date) => {
    setApplyDates(prev => ({
      ...prev,
      [day]: date
    }));
  };

  const removeDrill = (day, index) => {
    setSelectedDrills(prev => {
      const updated = [...prev[day]];
      updated.splice(index, 1);
      return { ...prev, [day]: updated };
    });
  };

  const handleWeekSelect = (date) => {
    setSelectedStartDate(startOfWeek(date, { weekStartsOn: 1 }));
    setShowWeekPicker(false);
  };

  const renderCalendar = () => {
    const monthStart = startOfWeek(currentMonth, { weekStartsOn: 1 });
    const monthEnd = addDays(monthStart, 41);
    const startDate = monthStart;
    const endDate = monthEnd;

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const isCurrentWeek = isSameWeek(cloneDay, selectedStartDate, { weekStartsOn: 1 });

        days.push(
          <div
            key={day.toString()}
            className={`calendar-day ${isCurrentWeek ? 'selected-week' : ''}`}
            onClick={() => handleWeekSelect(cloneDay)}
          >
            <span className="day-number">{format(day, 'd')}</span>
            {i === 0 && <span className="week-indicator">Week</span>}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="calendar-week">
          {days}
        </div>
      );
      days = [];
    }
    return (
      <div className="calendar-picker-modal">
        <div ref={calendarRef} className="calendar-picker-container" onClick={(e) => e.stopPropagation()}>
          <div className="calendar-header">
            <button
              onClick={(e) => {
                setCurrentMonth(addWeeks(currentMonth, -1));
              }}
              className="nav-button"
            >
              &lt;
            </button>
            <h3>{format(currentMonth, 'MMMM yyyy')}</h3>
            <button
              onClick={(e) => {
                setCurrentMonth(addWeeks(currentMonth, 1));
              }}
              className="nav-button"
            >
              &gt;
            </button>
          </div>
          <div className="calendar-weekdays">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="weekday-header">{day}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {rows}
          </div>
        </div>
      </div>
    );
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowWeekPicker(false);
      }
    };

    if (showWeekPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showWeekPicker]);
  const handleApply = async () => {
    setIsSaving(true);
    const mainCurriculum = weekDays.map((day) => {
      const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][day.getDay()];
      const date = format(day, 'yyyy-MM-dd');
      const drillIds = selectedDrills[dayKey]?.map(drill => drill.value) || [];
      return {
        curriculumId: curriculumIds[date] || null,
        date,
        drills: drillIds
      };
    });


    const additionalDates = Object.entries(applyDates)
      .flatMap(([dayKey, dates]) =>
        dates
          .filter(date => date !== '')
          .map(date => {
            const drillIds = selectedDrills[dayKey]?.map(drill => drill.value) || [];
            return {
              curriculumId: null,
              date,
              drills: drillIds
            };
          })
      );


    const payload = {
      sportId: selectedSport?.value || 0,
      level: level?.value?.toUpperCase() || '',
      subLevel: subLevel?.value || '',
      curriculum: [...mainCurriculum, ...additionalDates],
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_CREATE_CURRICULUM}?loginUserId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      await fetchCurriculum();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Curriculum submitted successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      setIsSaving(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Submission failed',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      console.error("Submission failed:", error);
      setIsSaving(false);
    }
  };



  const handleSportChange = (selectedOption) => {
    setSelectedSport(selectedOption);
    setLevel(null);
    setSubLevel(null);
    setSelectedCategory(null);
  };
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="enrollment-container" style={{ overflow: 'visible' }}>
      {showWeekPicker && renderCalendar()}
      <div >
        <div className="sales-filters-login">
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${selectedSport ? "has-value" : ""}`}
              classNamePrefix="react-select"
              options={sportsOptions}
              value={selectedSport}
              onChange={handleSportChange}
              placeholder="Select Sport"
              isClearable
              menuPortalTarget={document.body}
              styles={{
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {selectedSport && <label htmlFor="selectedSport">Sport Type</label>}
          </div>
          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${level ? "has-value" : ""}`}
              classNamePrefix="react-select"
              options={levelOptions}
              value={level}
              onChange={handleLevelChange}
              isDisabled={!selectedSport}
              placeholder="Select Proficiency"
              isClearable
              menuPortalTarget={document.body}
              styles={{
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {level && <label htmlFor="level">Proficiency</label>}
          </div>


          <div className="floating-select">
            <Select
              className={`sales-filter-court-booking ${subLevel ? "has-value" : ""}`}
              classNamePrefix="react-select"
              options={subLevelOptions}
              value={subLevel}
              onChange={handleSubLevelChange}
              placeholder="Select Sublevel"
              isClearable
              isDisabled={!level}
              menuPortalTarget={document.body}
              styles={{
                placeholder: (provided) => ({
                  ...provided,
                  color: !level ? 'grey' : 'black',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {subLevel && <label htmlFor="subLevel">Sublevel</label>}
          </div>
          <div className="floating-select">
            <button className="tt-action-btn" onClick={fetchCurriculum} style={{ height: '40px' }}>
              <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
            </button>
          </div>
        </div>
        {loading && (
          <div className="premium-loader">
            <div className="progress-track">
              <div className="progress-bar"></div>
            </div>
            <div className="status-text">
              <span>L</span>
              <span>O</span>
              <span>A</span>
              <span>D</span>
              <span>I</span>
              <span>N</span>
              <span>G</span>
            </div>
          </div>
        )}



        <div className="search-bar">
          <div className="week-selection">
            <label>Selected Week:</label>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowWeekPicker(prev => !prev);
              }}
              className="week-display-button"
            >
              {format(selectedStartDate, 'MMM d')} - {format(endOfWeek(selectedStartDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}
            </button>
          </div>
          {hasPrivilege(userData, 'CTU', 'CURRICULUM_MODIFY') && (
            <button
              onClick={async () => {
                if (isEditing) {
                  setIsSaving(true);
                  await handleApply();
                  setIsSaving(false);
                }
                setIsEditing(!isEditing);
              }}
              className="create-button"
              disabled={isSaving}
              style={{ width: window.innerWidth <= 600 ? "100px" : "auto" }}
            >
              {isEditing ? 'Save' : 'Edit'}
            </button>
          )}
        </div>

        {/* Weekly table */}
        <div className="payment-list-scroll-container" style={{ overflow: 'scroll' }}>
          <table className="schedule-table">
            <thead>
              <tr>
                {weekDays.map((day) => {
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  return (
                    <th key={day}>
                      <div className="day-name">{dayNames[day.getDay()]}</div>
                      <div className="day-date">{format(day, 'MM/dd')}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {weekDays.map((day) => {
                  const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][day.getDay()];
                  const availableDrills = getAvailableDrills(dayKey);

                  return (
                    <td key={day}>
                      <div className="drill-item-container">
                        {/* Selected drills */}
                        <div className="drill-items">
                          {selectedDrills[dayKey]?.map((drill, idx) => (
                            <div key={idx} className="drill-item">
                              <div>
                                <p className="drill-name">{drill.label} ({drill.categoryName})</p>
                              </div>
                              {isEditing && (
                                <button
                                  onClick={() => removeDrill(dayKey, idx)}
                                  className="remove-drill"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Drill dropdown */}
                        {isEditing && availableDrills.length > 0 && (
                          <div className="floating-select">
                            <Select
                              onChange={(selectedOption) => handleDrillSelect(dayKey, selectedOption)}
                              value={null}
                              options={getAvailableDrills(dayKey)}
                              placeholder="Add Drill"
                              isDisabled={!selectedSport}
                              className={`react-select-container ${selectedDrills ? "has-value" : ""}`}

                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                placeholder: (provided) => ({
                                  ...provided,
                                  color: !level ? 'grey' : 'black',
                                }),
                              }}
                              formatGroupLabel={(data) => (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{data.label}</span>
                                  <span style={{ color: '#999' }}>{data.options.length} drills</span>
                                </div>
                              )}
                            />
                          </div>
                        )}
                      </div>

                      {isEditing && (
                        <div className="apply-section">
                          <hr style={{ border: "1px solid #ccc", marginBottom: "10px" }} />
                          <div>
                            <label>Also Apply on</label>
                            {applyDates[dayKey].map((date, index) => (
                              <div key={index} className="date-input-container">
                                <input
                                  type="date"
                                  className="sales-filter-cirriculum"
                                  value={date}
                                  onChange={(e) => handleDateChange(dayKey, index, e.target.value)}
                                  onFocus={(e) => e.target.showPicker()}
                                />
                                <button
                                  onClick={() => removeDate(dayKey, index)}
                                  className="remove-date-button"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addDate(dayKey)}
                              className="add-date-button"
                            >
                              Add Date
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreateCurriculum;